import { useState } from "react";
import { toast } from "sonner";
import { Printer } from "lucide-react";
import { Empty, PortalHeading } from "./PortalLayout";
import { StatusPill } from "@/components/shared/QuoteTypeBadge";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg, fmtDate, money } from "@/lib/api";

const VersionTable = ({ v }) => (
  <div className="rounded-xl border p-4">
    <div className="flex justify-between text-xs text-[#56585E] mb-2"><span className="font-bold text-[#0D141A]">Version {v.version}</span><span className="capitalize">{v.status.replace(/_/g, " ")} · {fmtDate(v.created_at)}</span></div>
    {v.items.map((i, idx) => <div key={idx} className="flex justify-between text-sm py-1"><span>{i.label}</span><span>{i.total ? money(i.total) : "TBC"}</span></div>)}
    <div className="border-t mt-2 pt-2 flex justify-between font-bold text-sm">
      <span>Total</span>
      <span>{v.totals?.total ? money(v.totals.total) : v.totals?.total_min ? `${money(v.totals.total_min)} – ${money(v.totals.total_max)}` : "After assessment"}</span>
    </div>
    {v.note && <p className="text-xs text-[#56585E] mt-2">Advisor note: {v.note}</p>}
  </div>
);

const QuoteCard = ({ q, reload }) => {
  const [terms, setTerms] = useState(false);
  const latest = q.versions[q.versions.length - 1];
  const approvable = ["pending_review", "ready"].includes(q.status) && latest?.totals?.total && latest.status !== "superseded";
  const act = async (path, body) => {
    try { await api.post(`/quotes/${q.reference}/${path}`, body); toast.success(path === "approve" ? "Quote approved" : "Quote declined"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <article className="rounded-2xl bg-white p-6 print:shadow-none" data-testid={`quote-card-${q.reference}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><p className="text-xs font-bold tracking-widest text-[#F86900]">{q.reference}</p><h3 className="font-display font-bold text-lg">{q.service_title}</h3>
          <p className="text-sm text-[#56585E]">{[q.vehicle?.year, q.vehicle?.make, q.vehicle?.model].filter(Boolean).join(" ")} · requested {fmtDate(q.created_at)}</p></div>
        <div className="flex items-center gap-2"><StatusPill status={q.status} testId={`quote-status-${q.reference}`} />
          <button onClick={() => window.print()} data-testid={`quote-pdf-download-btn-${q.reference}`} className="p-2 rounded-full border hover:bg-black/5" aria-label="Print or save as PDF"><Printer className="h-4 w-4" /></button></div>
      </div>
      <div className="mt-4 grid md:grid-cols-2 gap-3">{q.versions.slice().reverse().map((v) => <VersionTable key={v.version} v={v} />)}</div>
      {approvable && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl bg-[#020165]/5 p-4">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="accent-[#F86900] h-4 w-4" data-testid={`quote-terms-${q.reference}`} />I accept the quote terms and booking policy</label>
          <button disabled={!terms} onClick={() => act("approve", { version: latest.version, accept_terms: true })} data-testid={`quote-approve-btn-${q.reference}`} className="ml-auto rounded-full bg-[#020165] text-white px-5 py-2 text-sm font-semibold disabled:opacity-40">Approve v{latest.version}</button>
          <button onClick={() => act("decline")} data-testid={`quote-decline-btn-${q.reference}`} className="rounded-full border px-5 py-2 text-sm font-semibold">Decline</button>
        </div>
      )}
      {q.status === "pending_review" && !approvable && <p className="mt-4 text-sm text-[#56585E]">A service advisor is preparing your written quote.</p>}
    </article>
  );
};

export default function Quotes() {
  const { data, reload } = useFetch("/quotes/mine");
  return (
    <div data-testid="portal-quotes">
      <PortalHeading title="Quotes & Estimates" sub="Every version is kept. Approve the one you're happy with." />
      {data && !data.quotes.length && <Empty testId="quotes-empty">No quotes yet.</Empty>}
      <div className="space-y-4">{data?.quotes.map((q) => <QuoteCard key={q.reference} q={q} reload={reload} />)}</div>
    </div>
  );
}
